import { cva } from 'class-variance-authority'
import { SidePanelResizeHandle } from 'designSystem/components/sidePanel/SidePanelResizeHandle'
import {
  SidePanelContext,
  type SidePanelContextValue,
  SidePanelDisplayStyle,
  type SidePanelDisplayStylePreference,
  type SidePanelMotion,
  SidePanelSide,
} from 'designSystem/components/sidePanelContext'
import {
  sidePanelAsideClassName,
  sidePanelContentClassName,
  sidePanelGridTemplateColumns,
  sidePanelMainStyle,
  sidePanelRootStyle,
} from 'designSystem/components/sidePanelLayout'
import {
  clampSidePanelWidth,
  resolveSidePanelMaxWidth,
  shouldPersistSidePanelStoredWidth,
  SIDE_PANEL_DEFAULT_MIN_MAIN_CONTENT_WIDTH,
  SIDE_PANEL_DEFAULT_MIN_WIDTH,
} from 'designSystem/components/sidePanelWidth'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import { useOpenWidthPreference } from 'designSystem/components/useOpenWidthPreference'
import { useSidePanelContainerFit } from 'designSystem/components/useSidePanelContainerFit'
import { useSidePanelDismiss } from 'designSystem/components/useSidePanelDismiss'
import { useSidePanelResize } from 'designSystem/components/useSidePanelResize'
import { cn } from 'designSystem/lib/utils'
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

const sidePanelRootVariants = cva(
  'relative grid h-full w-full min-h-0 min-w-0 overflow-hidden',
  {
    variants: {
      motion: {
        default: '',
        smooth:
          'transition-[grid-template-columns] duration-250 ease-drawer data-[panel-resizing=true]:transition-none motion-reduce:transition-none',
      },
    },
    defaultVariants: {
      motion: 'default',
    },
  }
)

export interface SidePanelProps {
  pageContent: ReactNode
  sidePanelContent: ReactNode

  open: boolean
  /**
   * Fired when the panel wants to dismiss itself — outside-click (sheet
   * style), ESC, or any chrome that calls the `close()` from context.
   * Opens are always parent-driven via the `open` prop.
   */
  onClose: () => void
  /**
   * When true, the panel acts modally: backdrop is shown over the rest of
   * the page, and outside-click / ESC close are suppressed. Use for
   * transactional steps that require an explicit dismiss.
   */
  asModal?: boolean

  side?: SidePanelSide
  displayStylePreference?: SidePanelDisplayStylePreference
  motion?: SidePanelMotion
  /** `inset` contains the panel within the page with a full border and radius. */
  variant?: 'flush' | 'inset'

  minViewportWidthForSidebar?: number
  minMainContentWidth?: number

  defaultWidth?: number
  /** Resets the panel to this share of its container each time it opens. */
  openWidthRatio?: number
  persistWidthKey?: string
  expandPanel?: boolean
  minWidth?: number
  maxWidth?: number
  /**
   * One-shot width hint from the panel's content (e.g. a screen that wants
   * to be wider). Applied only when the value changes; user-driven resizes
   * are not overridden.
   */
  preferredWidth?: number

  /** Only applies in sheet style. Defaults to true. */
  closeOnOutsideClick?: boolean
  closeOnEscape?: boolean

  className?: string
}

export function SidePanel({
  pageContent: children,
  sidePanelContent,
  open,
  onClose,
  asModal: modal = false,
  side = SidePanelSide.Right,
  displayStylePreference = 'auto',
  motion = 'default',
  variant = 'flush',
  minViewportWidthForSidebar: sheetBreakpoint = 1024,
  minMainContentWidth = SIDE_PANEL_DEFAULT_MIN_MAIN_CONTENT_WIDTH,
  defaultWidth,
  openWidthRatio,
  persistWidthKey,
  expandPanel = false,
  minWidth = SIDE_PANEL_DEFAULT_MIN_WIDTH,
  maxWidth: maxWidthProp,
  preferredWidth,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  className,
}: SidePanelProps) {
  const maxWidth = resolveSidePanelMaxWidth(maxWidthProp, openWidthRatio)
  const persistStoredWidth =
    persistWidthKey !== undefined ||
    shouldPersistSidePanelStoredWidth({
      maxWidthProp,
      openWidthRatio,
    })
  const panelLayoutRef = useRef<HTMLDivElement>(null)
  const asideRef = useRef<HTMLElement>(null)
  const skipNextOutsideClickRef = useRef(false)
  const draggingRef = useRef(false)

  const viewportAllowsSidebar = useMediaQuery(
    `(min-width: ${sheetBreakpoint ?? 0}px)`
  )
  const containerFitsSidebar = useSidePanelContainerFit({
    minMainContentWidth,
    minWidth,
    panelLayoutRef,
  })

  const { exitRatioMode, preferredPanelTrack, setWidth, width } =
    useOpenWidthPreference({
      defaultWidth,
      maxWidth,
      minWidth,
      open,
      openWidthRatio,
      panelLayoutRef,
      persistStoredWidth,
      preferredWidth,
      storageKey: persistWidthKey,
    })

  const displayStyle: SidePanelDisplayStyle =
    displayStylePreference === 'sheet'
      ? SidePanelDisplayStyle.Sheet
      : expandPanel || (viewportAllowsSidebar && containerFitsSidebar)
        ? SidePanelDisplayStyle.Sidebar
        : SidePanelDisplayStyle.Sheet

  useLayoutEffect(() => {
    const panelLayout = panelLayoutRef.current
    if (!panelLayout || draggingRef.current) return
    const clamped = clampSidePanelWidth({ maxWidth, minWidth, width })
    panelLayout.style.setProperty('--sp-w', `${clamped}px`)
  }, [maxWidth, minWidth, width])

  useLayoutEffect(() => {
    asideRef.current?.toggleAttribute('inert', !open)
  }, [open])

  useSidePanelDismiss({
    asideRef,
    closeOnEscape,
    closeOnOutsideClick,
    modal,
    onClose,
    open,
    panelLayoutRef,
    skipNextOutsideClickRef,
  })

  const { handleResizeKeyDown, handleResizePointerDown } = useSidePanelResize({
    draggingRef,
    exitRatioMode,
    maxWidth,
    minWidth,
    panelLayoutRef,
    persistStoredWidth,
    setWidth,
    side,
    skipNextOutsideClickRef,
    storageKey: persistWidthKey,
    width,
  })

  const keepOpenOnNextOutsideClick = useCallback(() => {
    skipNextOutsideClickRef.current = true
  }, [])

  const contextValue = useMemo<SidePanelContextValue>(
    () => ({
      displayStyle,
      open,
      close: onClose,
      keepOpenOnNextOutsideClick,
    }),
    [displayStyle, keepOpenOnNextOutsideClick, onClose, open]
  )

  const reserveColumn = displayStyle === SidePanelDisplayStyle.Sidebar && open
  const gridTemplateColumns = sidePanelGridTemplateColumns({
    displayStyle,
    expandPanel,
    maxWidth,
    preferredPanelTrack,
    reserveColumn,
    side,
  })

  return (
    <SidePanelContext.Provider value={contextValue}>
      <div
        ref={panelLayoutRef}
        data-slot='side-panel-root'
        data-panel-display-style={displayStyle}
        data-panel-open={open ? 'true' : 'false'}
        data-panel-modal={modal ? 'true' : 'false'}
        data-panel-side={side}
        data-panel-variant={variant}
        className={cn(sidePanelRootVariants({ motion }), className)}
        style={sidePanelRootStyle({
          gridTemplateColumns,
          maxWidth,
          minMainContentWidth,
          minWidth,
        })}>
        <div
          data-slot='side-panel-main'
          className='h-full min-h-0 min-w-0 overflow-auto'
          style={sidePanelMainStyle({ displayStyle, side })}>
          {children}
        </div>

        <aside
          ref={asideRef}
          data-slot='side-panel'
          aria-hidden={!open ? true : undefined}
          className={sidePanelAsideClassName({
            expandPanel,
            motion,
            side,
            variant,
          })}
          data-panel-display-style={displayStyle}
          data-panel-open={open ? 'true' : 'false'}
          data-panel-modal={modal ? 'true' : 'false'}
          data-panel-variant={variant}>
          {!expandPanel && (
            <SidePanelResizeHandle
              side={side}
              width={width}
              minWidth={minWidth}
              maxWidth={maxWidth}
              onPointerDown={handleResizePointerDown}
              onKeyDown={handleResizeKeyDown}
            />
          )}
          <div className={sidePanelContentClassName({ expandPanel, variant })}>
            {sidePanelContent}
          </div>
        </aside>

        {modal && (
          <div
            data-slot='side-panel-backdrop'
            aria-hidden
            className='fixed inset-0 z-40 bg-black/40'
          />
        )}
      </div>
    </SidePanelContext.Provider>
  )
}
